import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Chip,
  InputAdornment,
  MenuItem,
  Skeleton,
  Stack,
  TextField,
  Typography,
  Unstable_Grid2 as Grid
} from '@mui/material';
import { Groups, Search } from '@mui/icons-material';
import SEOPageTitle from 'components/shared/SEOPageTitle';
import OrgExpandableRow from 'components/org-client/OrgExpandableRow';
import useOrgPageTitle from 'components/org-client/useOrgPageTitle';
import useLogger from 'hooks/useLogger';
import { MY_ORGANIZATION_DEPARTMENTS, MY_ORGANIZATION_MEMBERS } from 'api/graphql/orgClient';

const SEARCH_DEBOUNCE_MS = 350;

const STATUS_TONE = {
  ACTIVE: { fg: 'sessionStatus.completed.fg', bg: 'sessionStatus.completed.bg', label: 'Activo' },
  IN_ARREARS: { fg: 'sessionStatus.pending.fg', bg: 'sessionStatus.pending.bg', label: 'En mora' },
  INACTIVE: { fg: 'text.secondary', bg: 'surface.overlay', label: 'Sin plan' }
};

const formatDate = (iso) => {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString('es-CO', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch {
    return null;
  }
};

function DetailField({ label, value }) {
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography
        variant="caption"
        sx={{ color: 'text.disabled', fontWeight: 700, letterSpacing: 0.3, textTransform: 'uppercase', display: 'block' }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 700, wordBreak: 'break-word' }}>
        {value}
      </Typography>
    </Box>
  );
}

DetailField.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.node.isRequired
};

/**
 * Directorio de colaboradores con el beneficio. Informativo: aquí se consulta quién está
 * cubierto y con qué plan, nunca su bienestar (eso solo existe agregado) ni acciones sobre
 * su cuenta. Altas y bajas las hace el equipo de OTRIX.
 */
function OrgClientMembers() {
  const { t } = useTranslation(['orgClient']);
  const { logError } = useLogger();
  const pageTitle = t('menu.members', { defaultValue: 'Colaboradores' });
  useOrgPageTitle(pageTitle);

  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [departmentId, setDepartmentId] = useState('');

  // La búsqueda la resuelve el backend: se espera a que el usuario deje de escribir para no
  // disparar una consulta por tecla.
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [search]);

  const { data, loading, error } = useQuery(MY_ORGANIZATION_MEMBERS, {
    variables: { search: debouncedSearch || null, department_id: departmentId || null },
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
    onError: (queryError) => logError('OrgClientMembers', queryError)
  });
  const { data: departmentsData } = useQuery(MY_ORGANIZATION_DEPARTMENTS, {
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
    onError: (queryError) => logError('OrgClientMembers', queryError)
  });

  const members = useMemo(() => data?.myOrganizationMembers || [], [data]);
  const departments = departmentsData?.myOrganizationDepartments || [];
  const hasFilters = Boolean(debouncedSearch || departmentId);

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <SEOPageTitle title={pageTitle} />

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} sx={{ mb: 2.5 }}>
        <TextField
          size="small"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder={t('members.searchPlaceholder', { defaultValue: 'Buscar por nombre o correo' })}
          InputProps={{
            // El buscador no lleva label visible (el placeholder describe la acción), así que
            // el nombre accesible va en el input nativo.
            inputProps: { 'aria-label': t('members.searchAria', { defaultValue: 'Buscar colaborador' }) },
            startAdornment: (
              <InputAdornment position="start">
                <Search sx={{ fontSize: 18, color: 'text.disabled' }} />
              </InputAdornment>
            )
          }}
          sx={{ flex: 1 }}
        />
        <TextField
          select
          size="small"
          label={t('filters.department', { defaultValue: 'Departamento' })}
          value={departmentId}
          onChange={(event) => setDepartmentId(event.target.value)}
          sx={{ minWidth: { xs: '100%', md: 220 } }}>
          <MenuItem value="">{t('filters.allDepartments', { defaultValue: 'Toda la empresa' })}</MenuItem>
          {departments.map((department) => (
            <MenuItem key={department.id} value={department.id}>
              {department.name}
            </MenuItem>
          ))}
        </TextField>
      </Stack>

      {error && members.length === 0 && (
        <Alert severity="error" variant="filled" sx={{ borderRadius: 3, mb: 2.5 }}>
          {t('errors.members', {
            defaultValue: 'No pudimos cargar tus colaboradores. Intenta de nuevo en un momento.'
          })}
        </Alert>
      )}

      {loading && members.length === 0 ? (
        <Stack spacing={1}>
          {[0, 1, 2, 3, 4].map((index) => (
            <Skeleton key={index} variant="rounded" height={68} sx={{ borderRadius: '14px' }} />
          ))}
        </Stack>
      ) : (
        <>
          <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 1.5 }}>
            {t('members.count', { count: members.length, defaultValue: '{{count}} colaboradores' })}
          </Typography>

          {members.length === 0 ? (
            <Box
              sx={{
                p: 4,
                borderRadius: 3,
                textAlign: 'center',
                border: '1px dashed',
                borderColor: 'hairline.strong',
                backgroundColor: 'surface.overlay'
              }}>
              <Groups sx={{ color: 'text.disabled', fontSize: 32 }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 800, mt: 1 }}>
                {hasFilters
                  ? t('members.emptyFilteredTitle', { defaultValue: 'Sin resultados' })
                  : t('members.emptyTitle', { defaultValue: 'Todavía no hay colaboradores' })}
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5, maxWidth: 460, mx: 'auto' }}>
                {hasFilters
                  ? t('members.emptyFilteredBody', { defaultValue: 'Prueba con otro nombre, correo o departamento.' })
                  : t('members.emptyBody', {
                      defaultValue:
                        'Escríbele al equipo de OTRIX para sumar personas de tu empresa al beneficio. En cuanto queden vinculadas, aparecen aquí.'
                    })}
              </Typography>
            </Box>
          ) : (
            <Stack spacing={1}>
              {members.map((member) => {
                const tone = STATUS_TONE[member.membership_status] || STATUS_TONE.INACTIVE;
                const joined = formatDate(member.organization_joined_at);

                return (
                  <OrgExpandableRow
                    key={member.id}
                    leading={
                      <Box
                        aria-hidden
                        sx={{
                          width: 38,
                          height: 38,
                          flex: 'none',
                          borderRadius: '50%',
                          display: 'grid',
                          placeItems: 'center',
                          fontWeight: 800,
                          color: 'text.secondary',
                          backgroundColor: 'surface.overlay',
                          border: '1px solid',
                          borderColor: 'hairline.main'
                        }}>
                        {(member.full_name || '?').trim().charAt(0).toUpperCase()}
                      </Box>
                    }
                    title={member.full_name}
                    subtitle={member.department?.name || t('members.noDepartment', { defaultValue: 'Sin departamento' })}
                    trailing={
                      <Chip
                        size="small"
                        label={t(`members.status.${member.membership_status}`, { defaultValue: tone.label })}
                        sx={{ flex: 'none', fontWeight: 700, color: tone.fg, backgroundColor: tone.bg }}
                      />
                    }>
                    <Grid container spacing={2} sx={{ pt: 1.5 }}>
                      <Grid xs={12} sm={6}>
                        <DetailField
                          label={t('members.email', { defaultValue: 'Correo' })}
                          value={member.email || '--'}
                        />
                      </Grid>
                      <Grid xs={12} sm={6}>
                        <DetailField
                          label={t('members.plan', { defaultValue: 'Plan' })}
                          value={member.plan_id?.name || t('members.noPlan', { defaultValue: 'Sin plan asignado' })}
                        />
                      </Grid>
                      <Grid xs={12} sm={6}>
                        <DetailField
                          label={t('filters.department', { defaultValue: 'Departamento' })}
                          value={member.department?.name || t('members.noDepartment', { defaultValue: 'Sin departamento' })}
                        />
                      </Grid>
                      <Grid xs={12} sm={6}>
                        <DetailField
                          label={t('members.joined', { defaultValue: 'Vinculado el' })}
                          value={joined || '--'}
                        />
                      </Grid>
                    </Grid>
                  </OrgExpandableRow>
                );
              })}
            </Stack>
          )}
        </>
      )}
    </Box>
  );
}

export default OrgClientMembers;
